/**
 * The router's location and the navigation capability, for the shell. The seed of a store
 * attaches the router: it writes the capability and the first location. After the seed, only the
 * router bridge (hooks/useRouterBridge.ts) writes the location. Thus, the store has a new
 * location in the same tick as the router.
 */

import { atom } from 'jotai';
import type { DataRouter, RouterState } from 'react-router-dom';
import { NavigationType } from 'react-router-dom';

import { copyUrlIntoRecordAtom, restoreViewFocusAtom } from './shellRecordAtoms';

interface ShellLocation {
    search: string;
    state: unknown;
    key: string;
    action: NavigationType;
}

// An object, because Jotai reads a bare function as an updater.
interface NavigationCapability {
    navigate: DataRouter['navigate'];
}

// The empty key differs from each router key, also from 'default', the key of a first entry.
const NO_LOCATION: ShellLocation = {
    search: '', state: null, key: '', action: NavigationType.Pop,
};

const NO_ROUTER: NavigationCapability = {
    navigate: (): never => {
        throw new Error('No router is attached to this store');
    },
};

/** The id of the route that renders the shell. */
export const SHELL_ROUTE_ID = 'shell';

const locationStateAtom = atom<ShellLocation>(NO_LOCATION);

const attachedEntryKeyAtom = atom<string>('');

const navigationStateAtom = atom<NavigationCapability>(NO_ROUTER);

// Read-only handles: the two write atoms below are the only writers.
export const shellLocationAtom = atom((get): ShellLocation => get(locationStateAtom));

export const navigationAtom = atom((get): NavigationCapability => get(navigationStateAtom));

/**
 * Takes a state of the router. A navigation, a fetcher or a blocker changes the state but keeps
 * the location, so only a new key wakes the readers. Another page's location is not the shell's:
 * the shell stays on screen until a lazy page loads. The same write keeps the record's copies of
 * the URL and, on a Back or a Forward, the saved front panel of the view.
 */
export const routerStateAtom = atom(
    null, (
        get, set, state: Pick<RouterState, 'location' | 'historyAction' | 'matches'>,
    ): void => {
        const { location, historyAction, matches } = state;
        if (location.key === get(locationStateAtom).key ||
            !matches.some((match) => match.route.id === SHELL_ROUTE_ID)) {
            return;
        }
        set(locationStateAtom, {
            search: location.search,
            state: location.state,
            key: location.key,
            action: historyAction,
        });
        set(copyUrlIntoRecordAtom, location);
        const isBackOrForward = historyAction === NavigationType.Pop &&
            location.key !== get(attachedEntryKeyAtom);
        if (isBackOrForward) {
            set(restoreViewFocusAtom, location.search);
        }
    });

/** Gives a new store the router's capability and its first state. */
export const attachRouterAtom = atom(null, (_get, set, router: DataRouter): void => {
    set(attachedEntryKeyAtom, router.state.location.key);
    set(navigationStateAtom, { navigate: router.navigate });
    set(routerStateAtom, router.state);
});
